/**
 * Chat text filter (docs/superpowers/specs/2026-09-30-social-notify-chat-design.md, §2 «Текст»).
 * Runs on the server before a message is stored; the client may run it too for instant feedback.
 *
 * 1. Trim; 1..CHAT_TEXT_MAX code points ('empty' / 'long').
 * 2. Links and contacts are REJECTED ('link'): URLs, domains (with or without a scheme), t.me / wa.me, @handles,
 *    e-mail, phone numbers (7+ digits with separators), WeChat / QQ ids («微信 xxx», «QQ 12345», «vx:», «wx:»);
 *    evasions: «t . me», «点com», «(dot)», full-width characters, spaced letters, Cyrillic look-alikes.
 * 3. Bad words (CHAT_BAD_WORDS: en / de / zh / ru — insults, obscenities, threats) are MASKED with «*» — the whole
 *    matched fragment, separators inside it included, as one solid group of the same length — with
 *    normalization: case, repeated letters, spaces / dots between letters, look-alike characters (0/o, 1/l/i,
 *    Latin/Cyrillic, leetspeak), diacritics. Latin/Cyrillic entries match whole words (or stems), so ordinary words
 *    that merely contain a bad substring ("assistant", "classic", «застрахуй») stay intact.
 * Emoji are never touched. The list is a starting set: moderation extends it.
 */

export const CHAT_TEXT_MIN = 1;
export const CHAT_TEXT_MAX = 500;

export type ChatTextCheck = { ok: true; text: string } | { ok: false; reason: 'link' | 'empty' | 'long' };

/* ------------------------------------------------------------------ word list ------------------------------------------------------------------ */

/**
 * Entry syntax: `word` — the whole word; `stem*` — a word starting with the stem; `*part*` — any word containing it
 * (only for parts that no innocent word contains); several words — a phrase. Chinese entries match anywhere.
 * Every entry is normalized the same way as the text (see `canon`), so write it in plain lower case.
 */
export const CHAT_BAD_WORDS: Readonly<Record<'en' | 'de' | 'zh' | 'ru', readonly string[]>> = {
  en: [
    '*fuck*', 'shit', 'shits', 'shitty', 'shithead*', 'bullshit', 'horseshit', 'bitch*', 'bastard*', '*asshole*',
    'ass', 'arse', 'arsehole*', 'dumbass', 'jackass', 'cunt*', 'dickhead*', '*cocksucker*', 'pussy', 'whore*',
    'slut*', 'fag', 'fags', 'faggot*', 'nigger*', 'nigga*', 'retard*', 'wanker*', 'twat*', 'prick', 'douche*',
    'idiot*', 'moron*', 'rape', 'raped', 'rapist*', 'kys', 'kill yourself', 'kill urself', 'hang yourself',
    'i will kill you', 'go die',
  ],
  de: [
    'fick*', 'arsch', 'arschloch*', 'arschgesicht*', 'hure', 'huren*', 'hurensohn*', 'fotze*', 'wichser*', 'wixer*',
    'schlampe*', 'missgeburt*', 'spast*', 'mongo', 'scheisse*', 'scheiss*', 'kacke', 'pisser*', 'vollidiot*',
    'trottel*', 'penner*', 'schwuchtel*', 'kanake*', 'neger*', 'drecksau*', 'dreckssau*', 'miststuck*',
    'verpiss dich', 'halt die fresse', 'fresse', 'stirb', 'verreck*', 'ich bring dich um', 'ich tote dich',
  ],
  zh: [
    '傻逼', '傻b', '傻比', '煞笔', '沙比', '傻叉', '草泥马', '操你妈', '操你娘', '操你大爷', '肏', '艹', '屌',
    '他妈的', '去你妈的', '你妈逼', '妈逼', '日你妈', '日你娘', '狗日的', '狗娘养的', '王八蛋', '混蛋', '贱人',
    '婊子', '畜生', '鸡巴', '脑残', '智障', '白痴', '蠢货', '废物', '滚蛋', '去死', '杀了你', '弄死你', '砍死你',
  ],
  ru: [
    'хуй*', 'хуе*', 'хуя*', 'нахуй*', 'похуй*', 'охуе*', 'нихуя', 'дохуя', '*пизд*', 'бля', 'бляд*', 'блять',
    'еба*', 'ебл*', 'ебу*', 'заеб*', 'выеб*', 'уеб*', 'отъеб*', 'съеб*', 'наеб*', 'разъеб*', 'долбоеб*', 'мудак*',
    'мудил*', 'сука', 'суки', 'суку', 'сукин*', 'гандон*', 'гондон*', 'пидор*', 'пидар*', 'шлюх*', 'ублюд*',
    'дебил*', 'мраз*', 'чмо', 'тварь', 'залуп*', 'манда', 'дроч*', 'говн*', 'убью', 'сдохни', 'сдохнешь',
    'blyat', 'blyad', 'suka', 'pizd*', 'nahui', 'nahuy',
  ],
};

/* ---------------------------------------------------------------- normalization ---------------------------------------------------------------- */

const ZERO_WIDTH = /[\u00AD\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const BIDI = /[\u202A-\u202E\u2066-\u2069]/g;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/** Cyrillic / Greek letters that look like Latin ones → the Latin letter (б → 6, like the digit) */
const LOOKALIKE: Record<string, string> = {
  а: 'a', в: 'b', е: 'e', к: 'k', м: 'm', н: 'h', о: 'o', р: 'p', с: 'c', т: 't', у: 'y', х: 'x', і: 'i', ї: 'i',
  ј: 'j', ѕ: 's', ԁ: 'd', ԛ: 'q', ԝ: 'w', б: '6',
  α: 'a', β: 'b', ε: 'e', ι: 'i', κ: 'k', ν: 'v', ο: 'o', ρ: 'p', τ: 't', υ: 'y', χ: 'x',
};

/** Lower case, full-width → ASCII (NFKC), no diacritics or invisible characters, ß → ss. */
function base(s: string): string {
  return s
    .normalize('NFKC')
    .replace(ZERO_WIDTH, '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .normalize('NFC')
    .replace(/ß/g, 'ss');
}

/**
 * Two readings of leetspeak digits: Latin (3 → e, 4 → a, 6 → b) and Cyrillic (3 → з, 4 → ч, 6 → б).
 * Both readings map 0 → o, 1 / l / | / ! → i, 5 / $ → s, 7 → t, @ → a; `*` stays (a wildcard: «f*ck»).
 */
type Variant = 0 | 1;
const LEET_COMMON: Record<string, string> = { '0': 'o', '1': 'i', l: 'i', '|': 'i', '!': 'i', '5': 's', $: 's', '7': 't', '@': 'a' };
const LEET_VARIANT: [Record<string, string>, Record<string, string>] = [
  { '3': 'e', '4': 'a', '6': 'b' },
  { '3': 'з', '4': 'ч', '6': '6' },
];

function canonChar(ch: string, v: Variant): string {
  const l = LOOKALIKE[ch];
  if (l !== undefined) return l;
  return LEET_VARIANT[v][ch] ?? LEET_COMMON[ch] ?? ch;
}

/** a normalized word for the word list (the same mapping as the text) */
function canonWord(word: string, v: Variant): string {
  let out = '';
  for (const ch of base(word)) out += ch === ' ' ? ' ' : canonChar(ch, v);
  return out;
}

/* ------------------------------------------------------------------ word masks ------------------------------------------------------------------ */

const LEET_SYMBOLS = /^[@$!|*]$/;
/** joiners inside a word: «f.u.c.k», «f-u-c-k», «f_u_c_k» */
const JOINERS = /^[.\-_·•'’`~^/\\]$/;
const CJK = /[㐀-鿿豈-﫿]/;

/** Latin / Cyrillic entries match word boundaries and mask the whole word; Chinese ones mask just the match. */
type Entry = RegExp;

function escapeRe(ch: string): string {
  return ch.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
}

/** `ass` → `[a*]+[s*]{2,}`: every letter may repeat («fuuuck»), `*` in the text matches any letter («f*ck»). */
function entryPattern(canon: string): string {
  let out = '';
  for (let i = 0; i < canon.length; ) {
    const ch = canon[i]!;
    let k = 1;
    while (canon[i + k] === ch) k++;
    out += ch === ' ' ? ' ' : `[${escapeRe(ch)}*]${k === 1 ? '+' : `{${k},}`}`;
    i += k;
  }
  return out;
}

let compiled: [Entry[], Entry[]] | null = null;

function entries(): [Entry[], Entry[]] {
  if (compiled) return compiled;
  const build = (v: Variant): Entry[] => {
    const list: Entry[] = [];
    for (const lang of Object.values(CHAT_BAD_WORDS)) {
      for (const raw of lang) {
        const prefix = raw.startsWith('*');
        const suffix = raw.endsWith('*');
        const word = raw.replace(/^\*|\*$/g, '');
        const body = entryPattern(canonWord(word, v));
        if (CJK.test(word)) list.push(new RegExp(body, 'gu'));
        else {
          const head = prefix ? '[^ ]*' : '(?<![^ ])';
          const tail = suffix ? '[^ ]*' : '(?![^ ])';
          list.push(new RegExp(head + body + tail, 'gu'));
        }
      }
    }
    return list;
  };
  compiled = [build(0), build(1)];
  return compiled;
}

interface Token {
  /** base() of each source code point, with its index */
  chars: { c: string; i: number }[];
  /** source code points in the word */
  size: number;
  /** only spaces (and dots / dashes) separate it from the previous word */
  softGap: boolean;
}

/** Words of the text: letters / digits, leet symbols inside a word, joiners between letters. */
function tokenize(cps: string[]): Token[] {
  const bases = cps.map(base);
  const isWord = (i: number): boolean => {
    const b = bases[i] ?? '';
    if (LETTER_OR_DIGIT.test(b)) return true;
    // a leet symbol belongs to the word only when a letter or digit follows («sh!t», «@ss», but not «shit!»)
    return LEET_SYMBOLS.test(b) && LETTER_OR_DIGIT.test(bases[i + 1] ?? '');
  };
  const tokens: Token[] = [];
  let cur: Token | null = null;
  let soft = false;
  for (let i = 0; i < cps.length; i++) {
    if (isWord(i)) {
      if (!cur) {
        cur = { chars: [], size: 0, softGap: soft && tokens.length > 0 };
        tokens.push(cur);
      }
      for (const c of bases[i]!) if (c.trim() !== '') cur.chars.push({ c, i });
      cur.size++;
      continue;
    }
    const b = bases[i]!;
    if (cur && JOINERS.test(b)) {
      let j = i;
      while (j < cps.length && JOINERS.test(bases[j]!)) j++;
      if (isWord(j)) {
        i = j - 1;
        continue;
      }
    }
    const isSoft = b.trim() === '' || JOINERS.test(b);
    soft = cur ? isSoft : soft && isSoft;
    cur = null;
  }
  // «f u c k», «б л я д ь», «傻 逼»: runs of single characters become one word
  const out: Token[] = [];
  let run = false;
  for (const t of tokens) {
    const prev = out[out.length - 1];
    if (prev && t.size === 1 && t.softGap && (run || prev.size === 1)) {
      prev.chars.push(...t.chars);
      prev.size++;
      run = true;
      continue;
    }
    out.push(t);
    run = false;
  }
  return out;
}

function maskWords(text: string): string {
  const cps = Array.from(text);
  const tokens = tokenize(cps);
  const masked = new Set<number>();
  const byVariant = entries();
  for (const v of [0, 1] as const) {
    // stream: canonical words joined by single spaces; map[k] = source index of stream char k (-1 for the space)
    let stream = '';
    const map: number[] = [];
    tokens.forEach((t, n) => {
      if (n > 0) {
        stream += ' ';
        map.push(-1);
      }
      for (const { c, i } of t.chars) {
        for (const ch of canonChar(c, v)) {
          stream += ch;
          map.push(i);
        }
      }
    });
    for (const e of byVariant[v]) {
      for (const m of stream.matchAll(e)) {
        let from = Infinity;
        let to = -1;
        for (let k = m.index; k < m.index + m[0].length; k++) {
          const i = map[k]!;
          if (i < 0) continue;
          from = Math.min(from, i);
          to = Math.max(to, i);
        }
        // the whole fragment, separators included («f u c k» → «*******»), so the mask reads as one solid group;
        // only line breaks stay, to keep the message layout
        for (let i = from; i <= to; i++) if (cps[i] !== '\n') masked.add(i);
      }
    }
  }
  if (masked.size === 0) return text;
  return cps.map((ch, i) => (masked.has(i) ? '*' : ch)).join('');
}

/* ------------------------------------------------------------------ links ------------------------------------------------------------------ */

/** TLDs that are not ordinary words: found even with spaces around the dot («example . com») */
const TLD_STRONG = 'com|org|cn|ru|xyz|io|biz|ua|su|onion|рф|ру|中国|公司|网络|在线';
/** TLDs that are also words or abbreviations: only written tight («name.me», not «home. me») */
const TLD_TIGHT =
  'net|me|de|co|uk|eu|jp|kr|hk|tw|ly|gg|tv|fm|ai|im|cc|top|vip|info|app|dev|pro|live|store|shop|site|online|club|' +
  'link|fun|win|bet|casino|ws|tk|ml|ga|cf|gq|icu|cyou|ltd|work|cloud|space|website|asia|xin|page|vn|kz|by|pl|fr|es|nl';
const AFTER = String.raw`(?![\p{L}\p{N}-])`;
const LINK_TIGHT: RegExp[] = [
  /(?:https?|hxxps?|ftp)\s*:\s*\/\s*\//u,
  /(?<![\p{L}\p{N}])www\s*\./u,
  new RegExp(String.raw`[\p{L}\p{N}]\.(?:${TLD_STRONG})${AFTER}`, 'u'),
  // t.me, wa.me, telegram.me — also «t . m e»; not «don't. Me»
  /(?<![\p{L}\p{N}'’])(?:t|wa|telegram|tg)\s*\.\s*m\s*e(?![\p{L}\p{N}])/u,
  /(?<![\p{L}\p{N}])tg\s*:\s*\/\//u,
];
const LINK_LOOSE: RegExp[] = [
  new RegExp(String.raw`[\p{L}\p{N}][\p{L}\p{N}-]*[\p{L}\p{N}]\.(?:${TLD_TIGHT})${AFTER}`, 'u'),
  // @handle (Telegram, Instagram…) and e-mail
  /(?<![\p{L}\p{N}_.])@[a-z0-9_][a-z0-9_.]{2,}/u,
  /[a-z0-9._%+-]+@[a-z0-9-]+\.[a-z]{2,}/u,
];
/** WeChat / QQ / messenger ids */
const CONTACT: RegExp[] = [
  // «vx:», «wx:», «微信：», «telegram:» — a contact label followed by a colon
  /(?<![a-z])(?:vx|wx|v信|微信|威信|薇信|徽信|weixin|wechat|qq|扣扣|企鹅|telegram|whatsapp|viber|skype|discord|kakao|电报|телеграм|телега|вайбер|ватсап|вотсап)\s*(?:号码?|号|id)?\s*[:：=]/u,
  // «QQ 12345», «微信 wx123456»: a label and an id with a digit
  /(?<![a-z])(?:vx|wx|v信|微信|威信|薇信|徽信|weixin|wechat|qq|扣扣|企鹅)\s*(?:号码?|号|id|是|号是)?\s*(?=[a-z0-9_-]*\d)[a-z0-9_-]{5,}/u,
  // «微信 abcdef», «vx abc_def»: a Chinese label (or vx / wx) and a WeChat id (a letter + 5..19 letters, digits, _ -)
  /(?<![a-z])(?:vx|wx|v信|微信|威信|薇信|徽信)\s*(?:号码?|号|id|是|号是)?\s*[a-z][a-z0-9_-]{5,19}(?![a-z0-9_-])/u,
  // «加我v», «+vx», «加微», «加q»
  /(?:加|\+)\s*(?:我\s*)?(?:v|vx|wx|微|q|qq|扣)(?![a-z])/u,
];

const CN_DIGITS: Record<string, string> = { 零: '0', 〇: '0', 一: '1', 二: '2', 三: '3', 四: '4', 五: '5', 六: '6', 七: '7', 八: '8', 九: '9' };
const PHONE = /\+?(?<!\d)\d(?:[\s\-.()/]{0,3}\d){6,}(?!\d)/gu;
/** «1 000 000», «1.000.000» — an amount with thousands groups (not starting with 0) */
const GROUPED_AMOUNT = /^[1-9]\d{0,2}([ .\u00A0\u202F])\d{3}(?:\1\d{3})*$/;
const DATE = /^(?:\d{4}([-./])\d{1,2}\1\d{1,2}|\d{1,2}([-./])\d{1,2}\2\d{4})$/;

function hasPhone(s: string): boolean {
  const digits = s.replace(/[零〇一二三四五六七八九]/g, (c) => CN_DIGITS[c]!);
  for (const m of digits.matchAll(PHONE)) {
    const run = m[0];
    if (run.startsWith('+')) return true;
    if (GROUPED_AMOUNT.test(run) || DATE.test(run)) continue;
    return true;
  }
  return false;
}

/** Forms of the text for link search: as is and with look-alike Cyrillic letters read as Latin. */
function linkForms(text: string): { loose: string; tight: string }[] {
  const s = base(text)
    .replace(/[。｡·•・]/g, '.')
    // «(dot)», «[点]», «点com», « dot »
    .replace(/\s*[([{<]\s*(?:dot|点|點)\s*[)\]}>]\s*/g, '.')
    .replace(/\s*(?:点|點)\s*/g, '.')
    .replace(/(?<=\S)\s+dot\s+(?=\S)/g, '.');
  const latin = Array.from(s, (c) => (c === 'б' ? c : (LOOKALIKE[c] ?? c))).join('');
  return [s, latin].map((loose) => {
    const tight = loose
      .replace(/\s*\.\s*/g, '.')
      // «g o o g l e» → «google»
      .replace(/(?<![\p{L}\p{N}])([\p{L}\p{N}])\s+(?=[\p{L}\p{N}](?![\p{L}\p{N}]))/gu, '$1');
    return { loose, tight };
  });
}

function hasLink(text: string): boolean {
  if (hasPhone(base(text))) return true;
  for (const { loose, tight } of linkForms(text)) {
    if (LINK_TIGHT.some((re) => re.test(tight) || re.test(loose))) return true;
    if (LINK_LOOSE.some((re) => re.test(loose))) return true;
    if (CONTACT.some((re) => re.test(loose))) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ entry point ------------------------------------------------------------------ */

/**
 * Check a chat message. `ok: true` carries the text to store: trimmed, without control / invisible direction
 * characters, bad words masked. `ok: false` — reject with CHAT_REJECTED { reason }.
 */
export function checkChatText(input: string): ChatTextCheck {
  const text = input.replace(/\r\n?/g, '\n').replace(CONTROL, '').replace(BIDI, '').trim();
  const length = Array.from(text).length;
  if (length < CHAT_TEXT_MIN || base(text).trim() === '') return { ok: false, reason: 'empty' };
  if (length > CHAT_TEXT_MAX) return { ok: false, reason: 'long' };
  if (hasLink(text)) return { ok: false, reason: 'link' };
  return { ok: true, text: maskWords(text) };
}
